import { useLayoutEffect } from 'react';

// Counter coordinates stacked locks (modal + cart): inner unlock must not
// clobber the outer's saved state.
let lockCount = 0;
let savedHtmlOverflow = '';
let savedBodyOverflow = '';
let savedBodyTouchAction = '';
let savedBodyPaddingRight = '';

// Fixed right-anchored elements shift visibly when overflow:hidden resizes
// the ICB. Measure each pre/post lock and inject inline `right` to keep the
// screen rect stable (desktop +15, some mobile Chromium -5, overlay 0).
const PINNED_SELECTORS = '.site-nav--floating, .ats-floating-filters';
const pinnedSavedRight: Map<HTMLElement, string> = new Map();

export function useBodyScrollLock(active: boolean) {
    // useLayoutEffect so measure -> apply -> compensate finishes before paint.
    useLayoutEffect(() => {
        if (!active) { return; }
        if (lockCount === 0) {
            const pinned = Array.from(
                document.querySelectorAll<HTMLElement>(PINNED_SELECTORS),
            );
            const preRight = pinned.map(el => el.getBoundingClientRect().right);
            const preBodyRight = document.body.getBoundingClientRect().right;

            savedHtmlOverflow = document.documentElement.style.overflow;
            savedBodyOverflow = document.body.style.overflow;
            savedBodyTouchAction = document.body.style.touchAction;
            savedBodyPaddingRight = document.body.style.paddingRight;
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
            document.body.classList.add('scroll-locked');

            pinned.forEach((el, i) => {
                const delta = preRight[i] - el.getBoundingClientRect().right;
                if (delta === 0) { return; }
                pinnedSavedRight.set(el, el.style.right);
                el.style.right = `${-delta}px`;
            });

            // Body grew (desktop scrollbar freed its gutter): padding-right
            // pulls in-flow content back. Negative direction (mobile shrink)
            // can't be expressed as positive padding and is absorbed by the
            // page's `html { overflow-y: scroll }` baseline.
            const bodyGrowth = document.body.getBoundingClientRect().right - preBodyRight;
            if (bodyGrowth > 0) {
                document.body.style.paddingRight = `${bodyGrowth}px`;
            }
        }
        lockCount += 1;
        return () => {
            lockCount -= 1;
            if (lockCount === 0) {
                document.documentElement.style.overflow = savedHtmlOverflow;
                document.body.style.overflow = savedBodyOverflow;
                document.body.style.touchAction = savedBodyTouchAction;
                document.body.style.paddingRight = savedBodyPaddingRight;
                document.body.classList.remove('scroll-locked');
                pinnedSavedRight.forEach((prev, el) => {
                    if (prev) { el.style.right = prev; }
                    else { el.style.removeProperty('right'); }
                });
                pinnedSavedRight.clear();
            }
        };
    }, [active]);
}
